/**
 * 회사/서비스 소개 — Heinrich (heinrich.dasancms.co.kr)
 */
import { Helmet } from 'react-helmet-async'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FiZap,
  FiShield,
  FiRefreshCw,
  FiLock,
  FiCpu,
  FiUsers,
  FiFileText,
  FiCamera,
  FiMessageSquare,
  FiCheckCircle,
  FiMail,
  FiPhone,
  FiMapPin,
  FiExternalLink,
  FiAward,
} from 'react-icons/fi'
import Card, { CardBody } from '../../components/ui/Card'
import { COMPANY } from '../../lib/company'
import Brand, { renderWithBrand } from '../../components/brand/Brand'
import OnboardingTourEmbed from '../../features/onboarding/OnboardingTourEmbed'
import { KoreaGovSymbol, GovMinistryLogo } from '../../components/common/GovLogos'

/* ── 핵심 가치 — title·desc 는 i18n 키 (t() 로 렌더링 시점 해석) ── */
const CORE_VALUES = [
  { icon: FiZap,       key: 'fieldReady',    color: 'text-amber-400',          bg: 'bg-amber-400/10' },
  { icon: FiShield,    key: 'legalAccuracy', color: 'text-brand-primary-400',  bg: 'bg-brand-primary/10' },
  { icon: FiLock,      key: 'dataProtection', color: 'text-green-400',         bg: 'bg-green-400/10' },
  { icon: FiRefreshCw, key: 'continuousAi',  color: 'text-brand-secondary-400', bg: 'bg-brand-secondary/10' },
] as const

/* ── 주요 기능 ── */
const FEATURES = [
  { icon: FiMessageSquare, key: 'chatbot' },
  { icon: FiFileText,      key: 'jsa' },
  { icon: FiCheckCircle,   key: 'tbm' },
  { icon: FiCamera,        key: 'imageAnalysis' },
  { icon: FiFileText,      key: 'riskAssessment' },
  { icon: FiFileText,      key: 'corrective' },
] as const

/* ── 주요 고객군 ── */
const CUSTOMERS = ['manufacturing', 'construction', 'safetyConsult', 'enterprise'] as const

/* ── 대한민국 정부 부처 수상 실적 ── */
const GOV_AWARDS = [
  {
    key: 'labor',
    agencyName: '고용노동부',
    awardName: '고용노동부 장관상',
    accentColor: 'amber' as const,
    isMain: true,
  },
  {
    key: 'interior',
    agencyName: '행정안전부',
    awardName: '행정안전부 우수상',
    accentColor: 'blue' as const,
    isMain: false,
  },
  {
    key: 'climate',
    agencyName: '기후부',
    awardName: '기후부 장려상',
    accentColor: 'emerald' as const,
    isMain: false,
  },
] as const

/* ── 기술 스택 요약 ── */
const TECH_STACK = [
  { icon: FiCpu,      key: 'gemini' },
  { icon: FiShield,   key: 'rag' },
  { icon: FiFileText, key: 'docEngine' },
  { icon: FiUsers,    key: 'multiPlatform' },
] as const

/* ── fade-up 모션 프리셋 ── */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay },
})

export default function About() {
  const { t } = useTranslation()
  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <title>{t('about.pageTitle', { name: COMPANY.service_name })}</title>
        <meta name="description" content={t('about.metaDescription')} />
        <link rel="canonical" href={`https://${COMPANY.domain}/about`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Heinrich" />
        <meta property="og:url" content={`https://${COMPANY.domain}/about`} />
        <meta property="og:title" content={t('about.pageTitle', { name: COMPANY.service_name })} />
        <meta property="og:description" content={t('about.ogDesc')} />
        <meta property="og:image" content={`https://${COMPANY.domain}/main-logo.png`} />
      </Helmet>

      {/* 온보딩 투어가 html.light 를 강제해도 소개 페이지 본문은 다크 유지(임베드만 라이트). */}
      <div className="onb-theme-dark">
      {/* ── Hero ── 에디토리얼 좌측 정렬 + 배경 사진 */}
      <section className="relative overflow-hidden bg-zinc-950 pt-28 pb-20 border-b border-white/[0.06]">
        {/* 배경 사진 — 산업안전 표준 책상 다큐멘터리 톤 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <img
            src="/assets/hero/about-hero-ambient.jpg"
            alt=""
            className="w-full h-full object-cover object-center opacity-25"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/60" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-zinc-950 to-transparent" />
        </div>

        <div className="container-custom max-w-5xl relative z-10">
          <div className="max-w-3xl">
            <div className="text-xs font-mono text-zinc-500 tracking-wider mb-5">
              {renderWithBrand(t('about.badgeAbout', { name: COMPANY.service_name }))}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-white mb-6 leading-[1.15]">
              {t('about.heroLinePart1')}{' '}
              <span className="text-zinc-400 font-medium">{t('about.heroMoment')}</span>
              <br className="hidden md:block" /> {t('about.heroLinePart2')}
            </h1>
            <p className="text-zinc-400 text-base md:text-lg leading-[1.8] max-w-2xl">
              {renderWithBrand(t('about.heroSubtitle'))}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white transition-colors"
              >
                {t('about.ctaStart')}
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold bg-white/[0.04] border border-white/[0.12] text-zinc-200 hover:bg-white/[0.08] hover:border-white/[0.20] transition-colors"
              >
                {t('about.ctaPricing')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── 미션 / 비전 — 좌(텍스트 2단) 우(이미지) ── */}
      <section className="py-20 bg-surface">
        <div className="container-custom max-w-6xl">
          <div className="grid md:grid-cols-[7fr_5fr] gap-10 lg:gap-14 items-stretch">
            {/* 좌측: 미션·비전 2단 */}
            <div className="grid grid-cols-1 gap-5">
              <motion.div {...fadeUp(0)}>
                <Card glass className="h-full">
                  <CardBody className="space-y-3">
                    <span className="text-xs font-mono text-amber-400 tracking-wider">
                      {t('about.missionBadge')}
                    </span>
                    <h2 className="text-2xl font-bold text-ink">
                      {t('about.missionTitle')}
                    </h2>
                    <p className="text-ink-muted leading-[1.8]">
                      {t('about.missionDesc')}
                    </p>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div {...fadeUp(0.08)}>
                <Card glass className="h-full">
                  <CardBody className="space-y-3">
                    <span className="text-xs font-mono text-amber-400 tracking-wider">
                      {t('about.visionBadge')}
                    </span>
                    <h2 className="text-2xl font-bold text-ink">
                      {t('about.visionTitle')}
                    </h2>
                    <p className="text-ink-muted leading-[1.8]">
                      {t('about.visionDesc')}
                    </p>
                  </CardBody>
                </Card>
              </motion.div>
            </div>

            {/* 우측: 워크스페이스 이미지 */}
            <div className="relative min-h-[280px] md:min-h-0 rounded-md overflow-hidden border border-surface-border/30">
              <img
                src="/assets/section/about-workspace.jpg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/60 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* ── 대한민국 정부 부처 수상 실적 ── */}
      <section id="awards" className="py-24 bg-zinc-950 border-y border-white/[0.06] scroll-mt-16">
        <div className="container-custom max-w-6xl">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <span className="text-[11px] font-semibold tracking-[0.3em] text-amber-400/90 uppercase">
              {t('about.awardsBadge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              {t('about.awardsTitle')}
            </h2>
            <p className="text-zinc-400 text-base leading-[1.8] max-w-2xl mx-auto">
              {t('about.awardsSubtitle')}
            </p>
          </motion.div>

          <div className="space-y-5">
            {/* ── 고용노동부 장관상 (메인 상패 — 금장 헤어라인·엠블럼 후광·메달 워터마크) ── */}
            <motion.div {...fadeUp(0.05)}>
              <div className="rounded-2xl p-[1px] bg-gradient-to-br from-amber-300/60 via-amber-500/15 to-transparent">
                <div className="relative rounded-2xl bg-zinc-950 overflow-hidden">
                  <img src="/assets/awards/laurel_gold.webp" alt="" aria-hidden loading="lazy"
                    className="absolute -right-14 -bottom-14 w-80 opacity-[0.14] mix-blend-screen pointer-events-none select-none" />
                  <div className="flex flex-col lg:flex-row">
                    {/* 좌: 수여기관 아이덴티티 — 후광 링 엠블럼 */}
                    <div className="lg:w-72 shrink-0 p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-white/[0.06] flex flex-col justify-center items-center text-center gap-5">
                      <div className="relative">
                        <div className="absolute inset-6 rounded-full bg-amber-400/20 blur-2xl" aria-hidden />
                        <img src="/assets/awards/medal_gold.webp" alt="고용노동부 장관상 메달 일러스트" loading="lazy"
                          className="relative w-44 h-44 object-cover [mask-image:radial-gradient(circle,black_52%,transparent_73%)]" />
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-zinc-500 tracking-[0.14em]">대한민국 정부 · 2026</div>
                        <div className="text-2xl font-bold text-white mt-1">고용노동부</div>
                      </div>
                      <span className="inline-flex px-5 py-2 rounded-full bg-gradient-to-b from-amber-400/25 to-amber-500/10 border border-amber-400/40 text-amber-300 text-base font-bold tracking-widest">
                        장관상
                      </span>
                    </div>

                    {/* 우: 수상 내용 */}
                    <div className="relative flex-1 p-8 lg:p-10 space-y-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs font-semibold text-amber-400/90 tracking-[0.08em] uppercase">
                          {t('about.awards.labor.title')}
                        </span>
                        <span className="h-1 w-1 rounded-full bg-zinc-600" />
                        <span className="text-xs font-medium text-zinc-500">
                          {t('about.awards.labor.category')}
                        </span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold leading-tight bg-gradient-to-r from-amber-200 via-amber-300 to-amber-400/80 bg-clip-text text-transparent">
                        {t('about.awards.labor.awardName')}
                      </h3>
                      <p className="text-zinc-300 text-sm md:text-base leading-[1.8]">
                        {t('about.awards.labor.desc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ── 행정안전부 · 기후부 (2열 상패) ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <motion.div {...fadeUp(0.1)}>
                <div className="h-full rounded-2xl p-[1px] bg-gradient-to-br from-sky-400/40 via-sky-500/10 to-transparent">
                  <div className="relative h-full rounded-2xl bg-zinc-950 p-7 flex flex-col justify-between overflow-hidden">
                    <FiAward className="absolute -right-5 -bottom-5 w-28 h-28 text-sky-400/[0.05]" aria-hidden />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img src="/assets/awards/medal_silver.webp" alt="" aria-hidden loading="lazy"
                            className="w-16 h-16 -my-2 object-cover [mask-image:radial-gradient(circle,black_50%,transparent_72%)]" />
                          <div>
                            <div className="text-[10.5px] font-medium text-zinc-500 tracking-[0.12em]">대한민국 정부 · 2025</div>
                            <div className="text-base font-bold text-white mt-0.5">행정안전부</div>
                          </div>
                        </div>
                        <span className="inline-flex px-3.5 py-1.5 rounded-full bg-sky-400/10 border border-sky-400/25 text-sky-300 text-sm font-bold tracking-wider">
                          우수상
                        </span>
                      </div>

                      <div className="pt-1">
                        <span className="text-xs font-semibold text-sky-300/90 tracking-[0.08em] uppercase">
                          {t('about.awards.interior.title')}
                        </span>
                        <h3 className="text-xl font-bold text-white mt-1 mb-2">
                          {t('about.awards.interior.awardName')}
                        </h3>
                      </div>
                    </div>

                    <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-emerald-400 font-medium">
                        <FiCheckCircle size={13} /> 재난안전 데이터 활용 검증
                      </span>
                      <span className="font-medium text-zinc-500">{t('about.awards.interior.category')}</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div {...fadeUp(0.15)}>
                <div className="h-full rounded-2xl p-[1px] bg-gradient-to-br from-emerald-400/40 via-emerald-500/10 to-transparent">
                  <div className="relative h-full rounded-2xl bg-zinc-950 p-7 flex flex-col justify-between overflow-hidden">
                    <FiAward className="absolute -right-5 -bottom-5 w-28 h-28 text-emerald-400/[0.05]" aria-hidden />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img src="/assets/awards/medal_bronze.webp" alt="" aria-hidden loading="lazy"
                            className="w-16 h-16 -my-2 object-cover [mask-image:radial-gradient(circle,black_50%,transparent_72%)]" />
                          <div>
                            <div className="text-[10.5px] font-medium text-zinc-500 tracking-[0.12em]">대한민국 정부 · 2026</div>
                            <div className="text-base font-bold text-white mt-0.5">기후부</div>
                          </div>
                        </div>
                        <span className="inline-flex px-3.5 py-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/25 text-emerald-300 text-sm font-bold tracking-wider">
                          장려상
                        </span>
                      </div>

                      <div className="pt-1">
                        <span className="text-xs font-semibold text-emerald-300/90 tracking-[0.08em] uppercase">
                          {t('about.awards.climate.title')}
                        </span>
                        <h3 className="text-xl font-bold text-white mt-1 mb-2">
                          {t('about.awards.climate.awardName')}
                        </h3>
                      </div>
                    </div>

                    <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-emerald-400 font-medium">
                        <FiCheckCircle size={13} /> AX 인공지능 전환 혁신 인정
                      </span>
                      <span className="font-medium text-zinc-500">{t('about.awards.climate.category')}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Heinrich — 이름의 유산 ── */}
      <section className="py-20 bg-surface-deep">
        <div className="container-custom max-w-4xl">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">
              {t('whyHeinrich.badge')} <Brand className="normal-case tracking-normal" />
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-ink mt-3 mb-4">
              {renderWithBrand(t('whyHeinrich.title'))}
            </h2>
          </motion.div>

          <motion.div {...fadeUp(0.08)} className="space-y-6 mb-10">
            <p className="text-ink-muted text-base leading-[1.85]">{renderWithBrand(t('whyHeinrich.heritage1'))}</p>
            <p className="text-ink-muted text-base leading-[1.85]">{renderWithBrand(t('whyHeinrich.heritage2'))}</p>
            <p className="text-ink text-base leading-[1.85] font-medium">{renderWithBrand(t('whyHeinrich.heritage3'))}</p>
          </motion.div>

          <motion.div {...fadeUp(0.16)} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: t('whyHeinrich.statHeinrich'),  value: '75,000',   unit: t('whyHeinrich.unit'), color: 'text-ink-faint' },
              { label: t('whyHeinrich.statFacts'),     value: '24,000',   unit: t('whyHeinrich.unit'), color: 'text-ink-faint' },
              { label: t('whyHeinrich.statAcademic'),  value: '~20,000',  unit: t('whyHeinrich.unit'), color: 'text-ink-faint' },
              { label: t('whyHeinrich.statOurs'),      value: '400,000+', unit: t('whyHeinrich.unit'), color: 'text-amber-400' },
            ].map((s) => (
              <div key={s.label} className="rounded-md border border-surface-elevated/40 bg-surface-card p-5">
                <p className="text-[11px] text-ink-faint uppercase tracking-wider mb-1.5">{renderWithBrand(s.label)}</p>
                <p className={`text-2xl font-extrabold ${s.color}`}>
                  {s.value} <span className="text-sm font-normal text-ink-faint">{s.unit}</span>
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 데모 투어 임베드 (핵심 가치 섹션 위 · 명함 QR 유입 대응) ── */}
      <OnboardingTourEmbed
        scrollId="onb-about-scroll"
        eyebrow="제품 둘러보기 · 가입 없이"
        title="Heinrich를 실제 화면으로 둘러보세요"
        subtitle="예시 사업장 「Heinrich 정밀」의 실제 분석 결과로 위험성평가·JSA·작업허가서·TBM·법정의무까지 1분이면 확인됩니다. 지금 바로, 로그인 없이."
      />

      {/* ── 핵심 가치 ── */}
      <section className="py-20 bg-surface-deep">
        <div className="container-custom max-w-5xl">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-primary-400 uppercase tracking-widest">
              {t('about.coreValuesBadge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-ink mt-3 mb-4">
              {renderWithBrand(t('about.coreValuesTitle'))}
            </h2>
            <p className="text-ink-muted max-w-xl mx-auto">
              {renderWithBrand(t('about.coreValuesSubtitle'))}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {CORE_VALUES.map(({ icon: Icon, key, color, bg }, idx) => (
              <motion.div key={key} {...fadeUp(idx * 0.08)}>
                <Card hover className="h-full">
                  <CardBody className="flex gap-5">
                    <div
                      className={`shrink-0 h-12 w-12 rounded-md ${bg} flex items-center justify-center`}
                    >
                      <Icon size={22} className={color} />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-ink mb-2">{t(`about.coreValues.${key}.title`)}</h3>
                      <p className="text-sm text-ink-muted leading-[1.75]">{t(`about.coreValues.${key}.desc`)}</p>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 주요 기능 ── */}
      <section className="py-20 bg-surface">
        <div className="container-custom max-w-5xl">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-primary-400 uppercase tracking-widest">
              {t('about.featuresBadge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-ink mt-3 mb-4">
              {t('about.featuresTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, key }, idx) => (
              <motion.div key={key} {...fadeUp(idx * 0.07)}>
                <Card hover className="h-full">
                  <CardBody className="flex flex-col gap-3">
                    <div className="h-10 w-10 rounded-lg bg-brand-primary/15 flex items-center justify-center">
                      <Icon size={18} className="text-brand-primary-400" />
                    </div>
                    <h3 className="text-sm font-bold text-ink">{t(`about.features.${key}.title`)}</h3>
                    <p className="text-xs text-ink-muted leading-[1.7]">{t(`about.features.${key}.desc`)}</p>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 기술 스택 ── */}
      <section className="py-20 bg-surface-deep">
        <div className="container-custom max-w-5xl">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-secondary-400 uppercase tracking-widest">
              {t('about.technologyBadge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-ink mt-3 mb-4">
              {t('about.technologyTitle')}
            </h2>
            <p className="text-ink-muted max-w-xl mx-auto">
              {t('about.technologySubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {TECH_STACK.map(({ icon: Icon, key }, idx) => (
              <motion.div key={key} {...fadeUp(idx * 0.08)}>
                <Card glass className="h-full">
                  <CardBody className="flex gap-4">
                    <div className="shrink-0 h-10 w-10 rounded-lg bg-brand-secondary/10 flex items-center justify-center">
                      <Icon size={18} className="text-brand-secondary-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-ink mb-1.5">{t(`about.techStack.${key}.title`)}</h3>
                      <p className="text-sm text-ink-muted leading-[1.75]">{t(`about.techStack.${key}.desc`)}</p>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* 데이터 근거 배지 */}
          <motion.div {...fadeUp(0.2)} className="mt-8 flex flex-wrap justify-center gap-3">
            {(['badgeDb', 'badgeGemini', 'badgeLaws'] as const).map((k) => (
              <span
                key={k}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-card border border-surface-elevated/60 text-xs text-ink-muted"
              >
                <FiCheckCircle size={12} className="text-green-400" />
                {t(`about.techBadges.${k}`)}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 주요 고객군 ── */}
      <section className="py-20 bg-surface">
        <div className="container-custom max-w-5xl">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="text-xs font-bold text-brand-primary-400 uppercase tracking-widest">
              {t('about.customersBadge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-ink mt-3 mb-4">
              {t('about.customersTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {CUSTOMERS.map((key, idx) => (
              <motion.div key={key} {...fadeUp(idx * 0.07)}>
                <Card hover className="h-full text-center">
                  <CardBody className="flex flex-col items-center gap-2 py-7">
                    <FiUsers size={24} className="text-brand-primary-400" />
                    <h3 className="font-bold text-ink text-sm">{t(`about.customers.${key}.label`)}</h3>
                    <p className="text-xs text-ink-muted leading-[1.6]">{t(`about.customers.${key}.sub`)}</p>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 회사 정보 카드 ── */}
      <section className="py-20 bg-surface-deep">
        <div className="container-custom max-w-5xl">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <motion.div {...fadeUp()}>
              <span className="text-xs font-bold text-brand-primary-400 uppercase tracking-widest">
                {t('about.companyBadge')}
              </span>
              <h2 className="text-3xl font-bold text-ink mt-3 mb-5">{t('about.companyTitle')}</h2>

              <Card>
                <CardBody>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-surface-elevated/30">
                      {[
                        { label: t('about.labelBrand'), value: `${COMPANY.name} ${t('about.individualSuffix')}` },
                        { label: t('about.labelCeo'), value: COMPANY.ceo },
                        { label: t('about.labelBrn'), value: COMPANY.brn },
                        { label: t('about.labelAddress'), value: COMPANY.address },
                        { label: t('about.labelEstablished'), value: COMPANY.established },
                      ].map(({ label, value }) => (
                        <tr key={label}>
                          <td className="py-3 pr-4 text-ink-faint w-32 align-top">{label}</td>
                          <td className="py-3 text-ink">{value}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="py-3 pr-4 text-ink-faint align-top">{t('about.labelEmail')}</td>
                        <td className="py-3">
                          <a
                            href={`mailto:${COMPANY.email}`}
                            className="text-brand-primary-400 hover:underline inline-flex items-center gap-1"
                          >
                            <FiMail size={13} />
                            {COMPANY.email}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 text-ink-faint align-top">{t('about.labelPhone')}</td>
                        <td className="py-3">
                          <a
                            href={`tel:${COMPANY.phone}`}
                            className="text-ink hover:text-brand-primary-400 inline-flex items-center gap-1"
                          >
                            <FiPhone size={13} />
                            {COMPANY.phone}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 text-ink-faint align-top">{t('about.labelAddress')}</td>
                        <td className="py-3 text-ink inline-flex items-start gap-1">
                          <FiMapPin size={13} className="mt-0.5 shrink-0 text-ink-faint" />
                          {COMPANY.address}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-surface">
        <div className="container-custom max-w-5xl">
          <motion.div
            {...fadeUp()}
            className="rounded-xl bg-brand-gradient p-12 md:p-16 text-center text-white"
          >
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-5">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-amber-100 text-lg max-w-xl mx-auto mb-8 leading-[1.75]">
              {t('about.ctaDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md bg-white text-zinc-900 font-bold text-sm hover:bg-amber-50 transition-colors shadow-lg"
              >
                {t('about.ctaStart')}
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md bg-white/10 border border-white/30 text-white font-semibold text-sm hover:bg-white/20 transition-colors"
              >
                {t('about.ctaPricing')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      </div>
    </>
  )
}
